// src/utils/sessionQuality.js
//
// Session Quality alert data, shared by the Lifecycle and Slots tabs
// (components/SessionQualityAlert.jsx renders it). Pure.
//
//   passbacks      Prebid won with a sentinel/passback size (1x1, 9544x9544, ...)
//   slowCreatives  impression > 30 s after GAM, joined by a deterministic tier or ad unit
//   slowUncertain  the same gap joined by nearest-time only: shown for review, not counted
//   passbackSlow   passbacks that were also blank > 30 s

export const SLOW_RENDER_MS = 30000;
// Deterministic (xai/sai/correlator) + adUnit drive the headline count; nearest-time-only
// matches go to "needs verification" so a mis-bound beacon on a repeated iu can't raise
// a false >30s flag.
const CONFIDENT_TIERS = ['xai', 'sai', 'impCorrelator', 'correlator', 'adUnit'];
const EXACT_TIERS = ['xai', 'sai', 'impCorrelator', 'correlator'];

const isSlow = (lc) => !!(lc && lc.phases && lc.phases.impression && lc.phases.impression.fired
  && lc.phases.impression.latencyMs > SLOW_RENDER_MS);

export function slotLabelOf(lc) {
  if (lc && lc.slotLabel) return lc.slotLabel;
  const iu = (lc && lc.adUnit) || '';
  return iu.split('/').filter(Boolean).slice(-1)[0] || iu || '?';
}

/** How far to trust a render delay: the impression↔GAM join tier. */
export function matchConfidence(tier) {
  if (EXACT_TIERS.includes(tier)) return 'exact';
  if (tier === 'adUnit') return 'adUnit';
  return 'nearest';
}

export function buildSessionQuality(lifecycles) {
  const list = (Array.isArray(lifecycles) ? lifecycles : []).filter(Boolean);
  const passbacks = list.filter((lc) =>
    Array.isArray(lc.issues) && lc.issues.some((i) => i && i.msg && (i.msg.includes('sentinel') || i.msg.includes('passback')))
    && lc.phases && lc.phases.prebid && lc.phases.prebid.winner);
  const slowAll = list.filter((lc) => isSlow(lc) && !passbacks.includes(lc));
  const slowCreatives = slowAll.filter((lc) => CONFIDENT_TIERS.includes(lc.phases.impression.matchTier));
  const slowUncertain = slowAll.filter((lc) => !CONFIDENT_TIERS.includes(lc.phases.impression.matchTier));

  const byBidder = {};
  for (const lc of passbacks) {
    const seat = lc.phases.prebid.winner.seat || 'unknown';
    byBidder[seat] = (byBidder[seat] || 0) + 1;
  }
  return {
    passbacks,
    passbackSlow: passbacks.filter(isSlow),
    slowCreatives,
    slowUncertain,
    bidderSummary: Object.entries(byBidder).map(([b, n]) => `${b} ×${n}`).join(', '),
    totalCpm: passbacks.reduce((s, lc) => s + (Number(lc.phases.prebid.winner.price) || 0), 0),
    hasRealIncident: passbacks.length > 0 || slowCreatives.length > 0,
    empty: passbacks.length === 0 && slowAll.length === 0,
  };
}
